import { Alert, Box, Divider, Grid, Typography } from '@mui/material';
import { Formik } from 'formik';
import { type FC, type ReactElement, useContext } from 'react';

import { SlabContext } from '@core/context/SlabContext';
import {
  FormikCompany,
  NewCompanySchema,
  type RequiredCompanyFieldsInfo,
} from '@core/formik/company';
import { Company } from '@core/types/api/Company/Company';
import type { DispatchCustomer } from '@core/types/api/DispatchCustomer/DispatchCustomer';
import Enums from '@core/types/api/Enums';
import type { Market } from '@core/types/api/Market/Market';
import { FileEntityType } from '@core/types/file';
import { hasValue } from '@core/utils/equality';
import { FormErrorNotification, SetFormikValue } from '@core/utils/formikHelpers';
import { requiredLabel } from '@core/utils/strings';
import { NIL_UUID } from '@core/utils/uuid';
import { DrawerFormFooter } from '@web/components/DrawerFormFooter/DrawerFormFooter';
import { attachBufferedEntityFiles, useFileUploads } from '@web/components/Files';
import {
  InputMultiLookup,
  ReferenceValueMatcher,
} from '@web/components/InputMultiLookup/InputMultiLookup';
import { DrawerCloseHandler } from '@web/components/SlabDrawer/DrawerCloseHandler';
import { SlabDrawerHeader } from '@web/components/SlabDrawer/SlabDrawerHeader';
import { SlabDrawerLayout } from '@web/components/SlabDrawer/SlabDrawerLayout';
import { useDrawerCloseGuard } from '@web/components/SlabDrawer/useDrawerCloseGuard';
import { useSlabFormQuery } from '@web/hooks/useSlabFormQuery';
import { useToast } from '@web/providers/ToastProvider/useToast';

import { AddressField } from '../../components/Address/AddressField';
import { Input } from '../../components/Input/Input';
import { InputDropdown } from '../../components/InputDropdown/InputDropdown';
import { LoadingSpinner } from '../../components/LoadingSpinner/LoadingSpinner';
import { ApiLookupInput } from '../../components/LookupInput/ApiLookupInput';
import type { LookupInputOption } from '../../components/LookupInput/LookupInputSharedComponents';
import { SlabDrawer } from '../../components/SlabDrawer/SlabDrawer';
import { useDrawerArchive } from '../../hooks/useDrawerArchive';
import type { SharedDrawerOverrideProps } from '../../hooks/useDrawerManager';
import { useSlabMutation } from '../../hooks/useSlabMutation';
import { useSlabQuery } from '../../hooks/useSlabQuery';
import { DefaultEnsureDefined, enumLookups, lookups } from '../../utils/DomainHelpers';
import { DrawerTierBadges } from '../Products/components/DrawerTierBadges';
import { CompanyFilesSection } from './CompanyFilesSection';
import { DynamicDispatchCustomerLookup } from './components/DynamicDispatchCustomerLookup';

type Props = SharedDrawerOverrideProps<Company> & {
  newCompanyDispatchCustomer?: DispatchCustomer | undefined;
};

const companyCategoryOpts = enumLookups(Enums.CompanyCategory);

const CompanyDrawerPage: FC<Props> = ({
  resourceID,
  setIsOpen,
  onSave,
  onError,
  onClose,
  ensureDefined = DefaultEnsureDefined,
  newCompanyDispatchCustomer,
}) => {
  const ctx = useContext(SlabContext);
  const usesDispatchCustomer = ctx.userInfo.hasFlags([
    Enums.FeatureFlagName.FeatureFlagDispatchCustomer,
  ]);
  const areMarketsRequired = ctx.userInfo.hasFlags([
    Enums.FeatureFlagName.FeatureFlagRequiresCompanyMarkets,
  ]);
  const usesTierPricing = ctx.userInfo.hasFlags([Enums.FeatureFlagName.FeatureFlagTierPricing]);

  const isNew = resourceID === null;

  const { data: divisionList, isLoading: isLoadingDivisionList } = useSlabQuery(
    'GET divisions',
    {},
  );

  const { data: marketList, isLoading: isLoadingMarketList } = useSlabQuery('GET markets', {});

  const filesManager = useFileUploads({ entityType: FileEntityType.Company, entityId: null });
  const { guardClose, editTracker } = useDrawerCloseGuard();
  const filesToast = useToast({ subjectName: 'file' });

  const createNew = useSlabMutation('POST company', {
    onSuccess: (data) => {
      attachBufferedEntityFiles(filesManager, data.id, filesToast, FileEntityType.Company);
      onSave(data);
    },
    onError,
  });
  const updateExisting = useSlabMutation('PUT company by ID', {
    onSuccess: (data) => onSave(data),
    onError,
  });

  const { confirmAndArchive, isArchiveLoading, resolveIsArchived } = useDrawerArchive({
    entity: 'POST archive company by ID',
    getIsArchived: (data) => data.isArchived,
    onSuccess: (data) => {
      setIsOpen(false);
      onSave(data);
    },
  });

  const {
    isLoading: isLoadingCompany,
    isError: isErrorCompany,
    data: maybeCompany,
  } = useSlabFormQuery(
    'GET company by ID',
    {
      pathParams: {
        id: resourceID ?? '',
      },
    },
    { enabled: !isNew },
  );

  if (isLoadingCompany || isLoadingMarketList || isLoadingDivisionList) {
    return <LoadingSpinner />;
  }

  if (isErrorCompany) {
    return (
      <Box padding='2rem'>
        <Typography variant='h1' fontSize='2rem'>
          Error loading company
        </Typography>
      </Box>
    );
  }

  if (!marketList || !divisionList) {
    return null;
  }

  const company = ensureDefined(maybeCompany);
  const isArchived = resolveIsArchived(maybeCompany?.isArchived ?? false);
  const formikCompany =
    isNew && newCompanyDispatchCustomer !== undefined
      ? FormikCompany({
          ...company,
          dispatchCustomers: [newCompanyDispatchCustomer],
          name: newCompanyDispatchCustomer.name,
          alternateID: newCompanyDispatchCustomer.code,
          address: newCompanyDispatchCustomer.address ?? company.address,
        })
      : FormikCompany(company);

  const marketOpts: LookupInputOption[] = lookups({
    ...marketList,
    label: (m: Market) => m.name,
  });

  const requiredFields: RequiredCompanyFieldsInfo = {
    markets: areMarketsRequired,
  };

  const schema = NewCompanySchema(requiredFields);

  return (
    <Formik
      validationSchema={schema}
      initialValues={formikCompany}
      onSubmit={async (values): Promise<void> => {
        const wireCompany = new Company({
          ...schema.cast(values),
          marketSummaries: values.markets.map((m) => ({
            id: m.id,
          })),
          dispatchCustomers: Object.values(values.dispatchCustomers)
            .filter((dc) => dc.id !== null)
            .map((dc) => ({
              ...dc,
              // The option does not contain an external ID, and the server cannot accept
              // the zero value of a URN yet, so set it to null.
              externalId: null,
            })),
        });

        if (formikCompany.id === NIL_UUID) {
          createNew.mutate({
            args: {
              body: wireCompany,
            },
          });
        } else {
          updateExisting.mutate({
            args: {
              pathParams: {
                id: formikCompany.id,
              },
              body: wireCompany,
            },
          });
        }
      }}
    >
      {(formikBag): ReactElement => {
        const closeDrawer = (): void => {
          formikBag.resetForm({ values: formikCompany });
          setIsOpen(false);
          onClose?.();
        };
        const requestClose = (): void =>
          guardClose({
            isDirty:
              !isArchived &&
              (editTracker.isEdited() ||
                filesManager.hasAttachableItems ||
                filesManager.hasPendingUploads),
            canSave: formikBag.isValid,
            isSaving: createNew.isPending || updateExisting.isPending,
            onSave: formikBag.submitForm,
            onDiscard: closeDrawer,
          });

        return (
          <SlabDrawerLayout>
            <DrawerCloseHandler onClose={requestClose} editTracker={editTracker} />
            <Box>
              <FormErrorNotification />
              <SlabDrawerHeader
                title={`${isNew ? 'Create ' : 'Edit '}a company`}
                onClose={requestClose}
              />
              <Box paddingY='1.25rem' display='flex' flexDirection='column' gap='1rem'>
                {newCompanyDispatchCustomer && (
                  <Alert severity='info'>
                    Data from the dispatch customer will be used to create the company.
                  </Alert>
                )}
                <Typography variant='h2'>Company details</Typography>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Input name='name' label='Company name*' />
                  </Grid>
                  <Grid item xs={6}>
                    <Input name='alternateID' label='ID' />
                  </Grid>
                </Grid>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <InputDropdown
                      label='Company category'
                      name='category'
                      options={companyCategoryOpts}
                    />
                  </Grid>

                  <Grid item xs={6}>
                    {marketOpts.length > 0 && (
                      <InputMultiLookup
                        disabled={formikBag.values.divisions.length > 0}
                        name='markets'
                        label={requiredLabel('Markets', areMarketsRequired)}
                        withCheckbox
                        tip='Select specific markets otherwise the company will be associated with all markets.'
                        options={marketOpts}
                        onMatchChange={(matches): void => {
                          SetFormikValue(
                            formikBag,
                            'markets',
                            matches.map(({ value, label }) => ({
                              id: value,
                              option: {
                                value,
                                label,
                              },
                            })),
                          );
                        }}
                        formValueMatcher={ReferenceValueMatcher}
                      />
                    )}
                  </Grid>
                  <Grid item xs={6}>
                    <ApiLookupInput
                      formState={formikBag.values}
                      label='Salesperson'
                      name='user'
                      route={{
                        barrel: 'GET user summaries',
                        args: (inputText, { constructQueryParams }) =>
                          constructQueryParams(inputText, {
                            filterBy: [
                              {
                                name: 'isSalesperson',
                                operation: Enums.FilterOperation.Equals,
                                values: [true],
                              },
                              {
                                name: 'isDeactivated',
                                operation: Enums.FilterOperation.Equals,
                                values: [false],
                              },
                            ],
                          }),
                        transform: (userList): LookupInputOption[] =>
                          lookups({
                            ...userList,
                            label: (u) => u.fullName(),
                          }),
                      }}
                    />
                  </Grid>
                </Grid>
              </Box>

              <Divider />

              <AddressField name='address' />

              <Input name='notes' label='Notes (Internal Only)' type='textarea' />
              {usesTierPricing &&
                maybeCompany &&
                hasValue(maybeCompany?.tiers) &&
                maybeCompany.tiers.length > 0 && <DrawerTierBadges tiers={maybeCompany.tiers} />}
              {!usesDispatchCustomer ? null : (
                <>
                  <Divider />
                  <Typography variant='h2'>Dispatch systems</Typography>
                  <DynamicDispatchCustomerLookup />
                </>
              )}
              <CompanyFilesSection companyID={resourceID} manager={filesManager} />
            </Box>
            <DrawerFormFooter
              isArchived={isArchived}
              isNew={isNew}
              hasEntity={maybeCompany !== undefined}
              isArchiveLoading={isArchiveLoading}
              isSaving={createNew.isPending || updateExisting.isPending}
              isSaveDisabled={
                (!isNew && !formikBag.dirty) || createNew.isPending || updateExisting.isPending
              }
              isResetDisabled={
                !formikBag.dirty ||
                createNew.isPending ||
                updateExisting.isPending ||
                isArchiveLoading
              }
              onArchive={(): void => {
                if (maybeCompany) confirmAndArchive({ id: maybeCompany.id, isArchived });
              }}
              onCancel={requestClose}
              onReset={(): void => formikBag.resetForm({ values: formikCompany })}
              onSave={formikBag.submitForm}
            />
          </SlabDrawerLayout>
        );
      }}
    </Formik>
  );
};

export const CompanyDrawer = (props: Props): ReactElement => (
  <SlabDrawer isOpen={props.isOpen} close={() => props.setIsOpen(false)}>
    <CompanyDrawerPage {...props} />
  </SlabDrawer>
);
